<!--
// app/components/pages/region-nations/BracketTablesNations.vue
-->
<script setup lang="ts">
import IconArrowBracketTablesWorldCup from "~/app/components/pages/region-world-cup/IconArrowBracketTablesWorldCup.vue";
import { useSeasons } from "~/app/composables/season/seasons";
import { useTeams } from "~/app/composables/teams";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { isPastDate } from "~/src/Shared/utils";
import type { MatchCenterData } from "~/src/match/domain/models/Match";
import type { Team } from "~/src/team/domain/models/Team";
import MatchItemBracketTablesNations from "~/app/components/pages/region-nations/MatchItemBracketTablesNations.vue";
import SliderWrapper from "~/app/components/Shared/SliderWrapper.vue";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";

type Props = {
  isHomeComponent?: boolean;
  seasonIdData?: number;
};

const props = defineProps<Props>();

const { currentSeasonTeams, currentSeason } = useCompetitionStore();
const { getSeasonTeams, useFetchParamsSeasonMatchCenterData } = useSeasons();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { teamArrToObjById } = useTeams();
const { locale } = useI18n();

const teamsObj = ref<Record<number, Team>>({});
const bracketData = ref<MatchCenterData[]>([]);
const bracketKey = ref(0);

const sliderWrapperRef = ref<InstanceType<typeof SliderWrapper> | null>(null);

const trophyImage = "https://cms-es.kingsleague.pro/wp-content/uploads/2024/12/kwc-nations-trophy.png";

const getBracketData = async () => {
  const _seasonId = props.seasonIdData ? props.seasonIdData : seasonId.value;

  await nextTick();

  if (currentSeason.id === _seasonId) {
    teamsObj.value = await teamArrToObjById(currentSeasonTeams);
  } else {
    const teams = await getSeasonTeams(_seasonId);
    teamsObj.value = await teamArrToObjById(teams);
  }

  const { path, opts } = useFetchParamsSeasonMatchCenterData(_seasonId, locale.value);
  const { data, error } = await useFetch(path, opts);

  if (error.value) {
    console.error(error.value);
    bracketData.value = [];
  } else {
    bracketData.value = data.value ? data.value : [];
  }

  bracketKey.value++;

  // Wait for DOM updates and manually trigger SliderWrapper re-initialization
  await nextTick();
  await nextTick(); // Double nextTick to ensure child components are fully rendered

  // Manually re-initialize the slider after data changes
  if (sliderWrapperRef.value) {
    sliderWrapperRef.value.initializeSlider();
  }
};

await getBracketData();
</script>

<template>
  <div v-if="!isHomeComponent" class="text-right">
    <seasons-filter
      v-model:season-id="seasonId"
      v-model:season-cluster-id="seasonClusterId"
      @change="getBracketData"
    />
  </div>

  <slider-wrapper
    v-if="bracketData.length"
    ref="sliderWrapperRef"
    :scroll-distance="204"
    :enable-bounce-animation="true"
    auto-scroll-selector=".round-col-container.active-stage:last-of-type"
    scrollable-selector="#tablesSlider"
  >
    <div
      id="tablesSlider"
      :key="bracketKey"
      class="w-full overflow-x-scroll overflow-y-hidden hide-scrollbar bigsm:px-4"
      :class="{ 'container-reduced': isHomeComponent }"
    >
      <div
        :class="[
          'grid grid-flow-col auto-cols-max',
          isHomeComponent ? 'gap-12' : 'gap-20',
        ]"
        :style="{ 'scale: 0.8': !isHomeComponent }"
      >
      <!--Round 1-->
      <div class="round-col-container">
        <h3 class="round-title text-white">{{ bracketData[0].turnName }}</h3>
        <div class="round-container nations-border-color">
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-nations
              v-for="match in bracketData[0].matches.sort((a, b) =>
                a.metaInformation.matchName.localeCompare(b.metaInformation.matchName)
              )"
              :key="match.id"
              :match="match"
              :teams-obj="teamsObj"
              :reduced-size="!!isHomeComponent"
            ></match-item-bracket-tables-nations>
          </div>
        </div>
      </div>

      <!--Round 2-->
      <div class="round-col-container" :class="{ 'active-stage': isPastDate(bracketData[0].finishDate) }">
        <h3 class="round-title text-white">{{ bracketData[1].turnName }}</h3>
        <div
          class="bg-gradient-to-t from-[#BF8838] to-[#FFD179] rounded-t-lg px-2 py-0.5 uppercase text-[#101F50] font-semibold"
        >
          {{ $t("bracketKwc.groupWinners") }}
        </div>
        <div class="round-container nations-border-color !mb-6 self-stretch">
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-nations
              v-for="match in bracketData[1].matches
                .sort((a, b) => a.metaInformation.matchName.localeCompare(b.metaInformation.matchName))
                .filter(m => m.metaInformation.previousRound === 'winner')"
              :key="match.id"
              :match="match"
              :teams-obj="teamsObj"
              :reduced-size="!!isHomeComponent"
            ></match-item-bracket-tables-nations>
          </div>

          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[3rem]' : 'w-[5rem]'"
            bg-color="#FFD179"
            top-position-class="top-1/2 !z-10"
            text-indicator="1-0"
            no-pointer
            indicator-color="text-[#FFD179]"
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[3.1rem]' : 'w-[5rem]'"
            bg-color="#3363FF"
            is-gradient-bg="repeating-linear-gradient(90deg, #3363FF, #3363FF 8px, transparent 8px, transparent 16px, #3363FF 14px);"
            top-position-class="bottom-1/4 !z-10"
            text-indicator="1-1"
            :right="locale !== 'ar'"
            no-pointer
            indicator-color="text-[#3363FF]"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[399px]' : 'w-[559px]'"
            bg-color="#FFD179"
            top-position-class="top-16 !z-10"
            text-indicator="2-0"
            no-pointer
            indicator-color="text-[#FFD179]"
            :right="locale !== 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div class="bg-white rounded-t-lg px-2 py-0.5 uppercase text-[#101F50] font-semibold">
          {{ $t("bracketKwc.groupLosers") }}
        </div>
        <div class="round-container border-dashed">
          <div class="flex flex-col items-stretch gap-4">
            <match-item-bracket-tables-nations
              v-for="match in bracketData[1].matches
                .sort((a, b) => a.metaInformation.matchName.localeCompare(b.metaInformation.matchName))
                .filter(m => m.metaInformation.previousRound === 'loser')"
              :key="match.id"
              :match="match"
              :teams-obj="teamsObj"
              :reduced-size="!!isHomeComponent"
            ></match-item-bracket-tables-nations>
          </div>

          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[3rem]' : 'w-[5rem]'"
            bg-color="#FFFFFF"
            is-gradient-bg="repeating-linear-gradient(90deg, #ffffff, #ffffff 8px, transparent 8px, transparent 16px, #ffffff 14px);"
            top-position-class="top-1/2 !z-10"
            text-indicator="0-1"
            no-pointer
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[3rem]' : 'w-[5rem]'"
            bg-color="#FFFFFF"
            is-gradient-bg="repeating-linear-gradient(90deg, #ffffff, #ffffff 8px, transparent 8px, transparent 16px, #ffffff 14px);"
            top-position-class="top-1/4 !z-10"
            text-indicator="1-1"
            :right="locale !== 'ar'"
            no-pointer
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-24' : 'w-40'"
            bg-color="#FFFFFF"
            is-gradient-bg="repeating-linear-gradient(90deg, #ffffff, #ffffff 8px, transparent 8px, transparent 16px, #ffffff 14px);"
            top-position-class="bottom-5 !z-10"
            text-indicator="0-2"
            :right="locale !== 'ar'"
            no-pointer
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>

      <!--Last Chance-->
      <div
        ref="lastChanceCol"
        class="round-col-container"
        :class="{ 'active-stage': isPastDate(bracketData[1].finishDate) }"
      >
        <h3 class="round-title text-white">Last Chance</h3>
        <div class="round-container border-dashed border-[#3363FF] bg-black -top-">
          <match-item-bracket-tables-nations
            v-for="match in bracketData[2].matches"
            :key="match.id"
            :match="match"
            :teams-obj="teamsObj"
            :reduced-size="!!isHomeComponent"
          ></match-item-bracket-tables-nations>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[3rem]' : 'w-[5rem]'"
            bg-color="#3363FF"
            is-gradient-bg="repeating-linear-gradient(90deg, #3363FF, #3363FF 8px, transparent 8px, transparent 16px, #3363FF 14px);"
            indicator-color="text-[#3363FF]"
            text-indicator="2-1"
            top-position-class="top-1/2 !z-10"
            no-pointer
            :right="locale !== 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div class="relative">
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[149px]' : 'w-[195px]'"
            bg-color="#FFFFFF"
            is-gradient-bg="repeating-linear-gradient(90deg, #3363FF, #3363FF 8px, transparent 8px, transparent 16px, #3363FF 14px);"
            :top-position-class="isHomeComponent ? '-top-[75px] !z-10' : '-top-20'"
            text-indicator="1-2"
            indicator-color="text-[#3363FF]"
            no-pointer
            vertical
          ></icon-arrow-bracket-tables-world-cup>
          <div
            class="self-stretch bg-[#1D42B5] text-lg text-[#101F50] font-semibold flex justify-center items-center p-2 min-w-24"
          >
            OUT
          </div>
        </div>
      </div>

      <!--cuartos-->
      <div
        class="round-col-container mb-[44px]"
        :class="{ 'active-stage': isPastDate(bracketData[2].finishDate) }"
      >
        <h3 class="round-title nations-color">
          {{ bracketData[3].turnName }}
        </h3>

        <div class="round-container nations-border-color !mb-1">
          <match-item-bracket-tables-nations
            v-for="match in bracketData[3].matches.slice(0, 2)"
            :key="match.id"
            :match="match"
            :teams-obj="teamsObj"
            :reduced-size="!!isHomeComponent"
          ></match-item-bracket-tables-nations>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[40px]' : 'w-[64px]'"
            bg-color="#FFD179"
            :top-position-class="isHomeComponent ? '-top-[21px] !z-10' : '-top-[34px] !z-10'"
            no-pointer
            vertical
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div class="round-container nations-border-color !mt-1">
          <match-item-bracket-tables-nations
            v-for="match in bracketData[3].matches.slice(2, 4)"
            :key="match.id"
            :match="match"
            :teams-obj="teamsObj"
            :reduced-size="!!isHomeComponent"
          ></match-item-bracket-tables-nations>
        </div>
      </div>

      <!--semis-->
      <div
        class="round-col-container mb-[44px]"
        :class="{ 'active-stage': isPastDate(bracketData[3].finishDate) }"
      >
        <h3 class="round-title nations-color">{{ bracketData[4].turnName }}</h3>
        <div
          class="bg-gradient-to-t from-[#BF8838] to-[#FFD179] rounded-t-lg px-2 py-0.5 uppercase text-[#101F50] font-semibold mt-auto"
        >
          Semifinal 1
        </div>
        <div class="round-container nations-border-color !mb-10 !mt-0">
          <match-item-bracket-tables-nations
            :match="bracketData[4].matches[0]"
            :teams-obj="teamsObj"
            :reduced-size="!!isHomeComponent"
          ></match-item-bracket-tables-nations>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[3rem]' : 'w-[5rem]'"
            bg-color="#FFD179"
            indicator-color="text-[#3363FF]"
            top-position-class="top-1/2 !z-10"
            no-pointer
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[1.5rem]' : 'w-[2.5rem]'"
            bg-color="#FFD179"
            top-position-class="top-1/2 !z-10"
            no-pointer
            :right="locale !== 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
        </div>

        <div
          class="bg-gradient-to-t from-[#BF8838] to-[#FFD179] rounded-t-lg px-2 py-0.5 uppercase text-[#101F50] font-semibold"
        >
          Semifinal 2
        </div>
        <div class="round-container nations-border-color !mt-0">
          <match-item-bracket-tables-nations
            :match="bracketData[4].matches[1]"
            :teams-obj="teamsObj"
            :reduced-size="!!isHomeComponent"
          ></match-item-bracket-tables-nations>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[3rem]' : 'w-[5rem]'"
            bg-color="#FFD179"
            indicator-color="text-[#3363FF]"
            top-position-class="top-1/2 !z-10"
            no-pointer
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[1.5rem]' : 'w-[2.5rem]'"
            bg-color="#FFD179"
            top-position-class="top-1/2 !z-10"
            no-pointer
            :right="locale !== 'ar'"
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>

      <!--final-->
      <div
        class="round-col-container mb-[44px]"
        :class="{ 'active-stage': isPastDate(bracketData[4].finishDate) }"
      >
        <h3 class="round-title nations-color">{{ bracketData[5].turnName }}</h3>
        <div class="round-container nations-border-color">
          <img
            :src="trophyImage"
            alt="Trophy Nations"
            width="90"
            height="110"
            class="absolute bottom-[110%] self-center"
          />
          <match-item-bracket-tables-nations
            v-for="match in bracketData[5].matches"
            :key="match.id"
            :match="match"
            :teams-obj="teamsObj"
            :reduced-size="!!isHomeComponent"
          ></match-item-bracket-tables-nations>
          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[1.5rem]' : 'w-[2.5rem]'"
            bg-color="#FFD179"
            top-position-class="top-1/2 !z-10"
            no-pointer
            :right="locale === 'ar'"
          ></icon-arrow-bracket-tables-world-cup>

          <icon-arrow-bracket-tables-world-cup
            :width-class="isHomeComponent ? 'w-[155px]' : 'w-[184px]'"
            bg-color="#FFD179"
            :top-position-class="
              isHomeComponent
                ? 'ltr:!-left-[50px] rtl:!-right-[104px] top-[56px] !z-10'
                : 'ltr:!-left-[82px] rtl:!-right-[135px] top-[70px] !z-10'
            "
            no-pointer
            vertical-no-centered
          ></icon-arrow-bracket-tables-world-cup>
        </div>
      </div>
      </div>
    </div>
  </slider-wrapper>
</template>

<style scoped>
.round-title {
  @apply font-['Termina'] font-semibold text-xl text-center uppercase mb-6;
}

.round-container {
  @apply border px-5 py-7 flex flex-col items-stretch gap-4 my-auto relative bg-[#1D42B5] bg-opacity-20 rounded-xl;
}

.round-col-container {
  @apply flex flex-col justify-stretch items-center;
}

.container-reduced {
  @apply my-4;
}

.container-reduced .round-title {
  @apply text-base;
}

.container-reduced .round-container {
  @apply !py-5 !px-3;
}
</style>
